import { loadScript } from '@gamiphy/utils/script';
import type { CartData } from '@gamiphy/loyalty-station-desktop/store/sdk/cart';
import type { UserInit } from '@gamiphy/loyalty-station-desktop/store/user';
import { APP_ID } from './constants';
import { getApiUrl, getCoreLoyaltySdkUrl } from '../helpers';

class TrayIntegration {
  private readonly loja = document.documentElement.getAttribute('data-store');

  private cartId?: string;

  private customerId?: string;

  constructor() {
    void this.initialize();
  }

  public async initialize(): Promise<void> {
    const appId = this.getAppId();
    if (!appId) {
      throw new Error('App id is not injected correctly');
    }

    // Retrieve the customer & Cart ids
    [
      this.customerId,
      this.cartId,
    ] = await Promise.all([
      this.getCustomerId(),
      this.getCartId(),
    ]);

    const customer = await this.getCustomer(appId);

    window.gamiphyAsyncInit = (sdk) => {
      void window.LoyaltyStation?.init({
        app: appId,
        ...customer && {
          user: customer,
        },
        goToAuth: (isSignUp) => {
          window.location.href = isSignUp ? '/cadastro' : '/my-account/login';
        },
        cart: {
          onLoadCart: async () => this.loadCart(),
          onApplyDiscount: async (cart, code: string) => this.applyDiscount(code),
        },
      });

      const productMetaTag = document.querySelector('meta[itemprop="productID"]');
      // check if it's the product page
      if (productMetaTag) {
        const productId = productMetaTag.getAttribute('content');
        if (productId) {
          // Track product viewed event
          void window.LoyaltyStation?.collectProductView(productId);
        }
      }

      document.querySelectorAll<HTMLFormElement>('form[action^="/loja/cartService.php"]')
        .forEach((form) => {
          form.addEventListener('submit', () => {
            const url = new URL(form.action, window.location.origin);
            const productId = url.searchParams.get('IdProd');
            if (productId) {
              void sdk.collectAddToCart(productId);
            }
          });
        });
    };
    void loadScript(getCoreLoyaltySdkUrl());
  }

  private async loadCart(): Promise<CartData> {
    if (!this.cartId) {
      throw new Error('Failed to get the cart id');
    }

    const response = await fetch(`/checkout/cart/api?session_id=${this.cartId}`);

    if (!response.ok) {
      throw new Error(`Failed to retrieve order data: ${response.status}`);
    }

    const data = await response.json() as {
      data: {
        cart: {
          checkout: {
            total: number
            subtotal: number
            couponDiscount: number
          }
          couponCode?: string
        }
      }
    };
    return {
      id: this.cartId,
      total: data.data.cart.checkout.total ?? 0,
      itemsTotal: data.data.cart.checkout.subtotal ?? 0,
      discountCodes: data.data.cart.couponCode ? [data.data.cart.couponCode] : [],
      items: [],
    };
  }

  private getAppId(): string | undefined {
    const scripts = Array.from(document.getElementsByTagName('script'));
    const trayMainScript = scripts.filter((script) => /.*\/sdk\/loyalty-station\/scripts\/tray.js/.test(script.src))[0];

    if (trayMainScript) {
      return new URL(trayMainScript.src).searchParams.get(APP_ID) ?? undefined;
    }

    return undefined;
  }

  private async getCustomerId(): Promise<string | undefined> {
    const response = await fetch('/mvc/store/greeting');
    if (!response.ok) {
      throw new Error('Failed to retrieve the customer id');
    }

    const data = await response.json() as {
      cartLoginToken: string,
      data?: {
        id?: string,
      }
    };

    return data.data?.id;
  }

  private async getCartId(): Promise<string> {
    const response = await fetch(`/nocache/info.php?loja=${this.loja}`);

    if (!response.ok) {
      throw new Error(`Failed to retrieve cart id: ${response.status}`);
    }

    const data = await response.json() as {
      _id: string
    };

    // eslint-disable-next-line no-underscore-dangle
    return data._id;
  }

  private async applyDiscount(code: string): Promise<{
    success: boolean
  }> {
    if (!this.cartId) {
      throw new Error('Failed to get the cart id');
    }

    if (!this.customerId) {
      throw new Error('Failed to get the customer id');
    }

    const body = new URLSearchParams();
    body.append('code', code);
    body.append('customer_id', this.customerId);

    const response = await fetch(`/checkout/api/coupon?session_id=${this.cartId}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
        Fingerprint: '4243200868',
      },
      body: body.toString(),
      credentials: 'same-origin',
    });

    return {
      success: response.ok,
    };
  }

  private async getCustomer(appId: string): Promise<UserInit | undefined> {
    if (!this.customerId) {
      return undefined;
    }

    const response = await fetch(`${getApiUrl()}/v2/users/tray/customer/${this.customerId}`, {
      headers: {
        'gamix-app-id': appId,
      },
    });
    if (!response.ok) {
      throw new Error('Failed to retrieve the customer information');
    }

    return await response.json() as UserInit;
  }
}

export default new TrayIntegration();
